import apiClient from './client';

export interface FxOrderListItem {
  id: string;
  userId?: string;
  clientName?: string;
  fromCurrency: string;
  toCurrency: string;
  fromAmount: number;
  toAmount: number;
  clientRate: number;
  markupValue: number;
  profitAmount?: number;
  profitCurrency?: string;
  status: string;
  reference?: string;
  providerName?: string;
  createdAt: string;
  paymentDate?: string;
  executedAt?: string;
}

export interface FxOrderDetail extends FxOrderListItem {
  providerRate: number;
  finalExecutedRate?: number;
  finalProviderRate?: number;
  indicativeRate?: number;
  indicativeFromAmount?: number;
  indicativeToAmount?: number;
  executionMode?: string;
  rateSource?: string;
  markupType?: string;
  providerId?: string;
  cancelReason?: string;
  rejectedReason?: string;
  failureReason?: string;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface IndicativeDailyRate {
  id: string;
  providerId: string;
  providerName: string;
  sourceCurrency: string;
  targetCurrency: string;
  indicativeBaseRate: number;
  indicativeClientRate: number;
  effectiveDate: string;
  validFrom: string;
  validTo?: string;
  isActive: boolean;
  createdAt: string;
}

export interface FxMarkupRule {
  id: string;
  providerId: string;
  providerName: string;
  sourceCurrency?: string;
  targetCurrency?: string;
  markupType: string;
  markupValue: number;
  minAmount?: number;
  maxAmount?: number;
  effectiveFrom: string;
  effectiveTo?: string;
  isActive: boolean;
  createdAt: string;
}

export interface FxQuoteResponse {
  transactionId: string;
  fromCurrency: string;
  toCurrency: string;
  fromAmount: number;
  toAmount: number;
  indicativeRate: number;
  providerRate: number;
  clientRate: number;
  providerSourceAmount?: number;
  markupValue: number;
  markupType: string;
  appliedMarkupRuleId?: string;
  providerName: string;
  quoteExpiresAt: string;
  rateSource?: string;
}

export interface FxQuotePreviewResponse {
  fromCurrency: string;
  toCurrency: string;
  fromAmount: number;
  toAmount: number;
  indicativeRate: number;
  providerRate: number;
  clientRate: number;
  providerSourceAmount?: number;
  markupValue: number;
  markupType: string;
  appliedMarkupRuleId?: string;
  providerName: string;
  rateSource?: string;
  quotedAt: string;
}

export const fxAdminApi = {
  // Orders
  getOrders: (params: { page?: number; pageSize?: number; status?: string; clientId?: string }) =>
    apiClient.get<PagedResult<FxOrderListItem>>('/admin/fx/orders', { params }),

  getOrder: (id: string) =>
    apiClient.get<FxOrderDetail>(`/admin/fx/orders/${id}`),

  quoteOrder: (data: {
    sourceClientAccountId: string;
    targetClientAccountId: string;
    amount: number;
    amountSide?: 'from' | 'to';
    customProviderRate?: number;
    customClientRate?: number;
    fixedMarkupAmount?: number;
    selectedMarkupRuleId?: string;
  }) => apiClient.post<FxQuoteResponse>('/admin/fx/orders/quote', data),

  previewOrder: (data: {
    sourceClientAccountId: string;
    targetClientAccountId: string;
    amount: number;
    amountSide?: 'from' | 'to';
    customProviderRate?: number;
    customClientRate?: number;
    fixedMarkupAmount?: number;
    selectedMarkupRuleId?: string;
  }) => apiClient.post<FxQuotePreviewResponse>('/admin/fx/orders/preview', data),

  createOrder: (data: {
    transactionId?: string;
    sourceClientAccountId: string;
    targetClientAccountId: string;
    amount: number;
    amountSide?: 'from' | 'to';
    customProviderRate?: number;
    customClientRate?: number;
    fixedMarkupAmount?: number;
    selectedMarkupRuleId?: string;
    paymentDate?: string;
  }) => apiClient.post<FxOrderDetail>('/admin/fx/orders/create', data),

  executeOrder: (data: {
    transactionId: string;
    providerRate?: number;
    clientRate?: number;
    providerTradeId?: string;
    providerQuoteId?: string;
    notes?: string;
  }) => apiClient.post<FxOrderDetail>('/admin/fx/orders/execute', data),

  rejectOrder: (data: { transactionId: string; reason: string }) =>
    apiClient.post('/admin/fx/orders/reject', data),

  // Rates
  getRates: () =>
    apiClient.get<IndicativeDailyRate[]>('/admin/fx/rates'),

  upsertRate: (data: {
    id?: string;
    providerId: string;
    sourceCurrency: string;
    targetCurrency: string;
    indicativeBaseRate: number;
    indicativeClientRate: number;
    effectiveDate?: string;
    validFrom?: string;
    validTo?: string;
  }) => apiClient.post<IndicativeDailyRate>('/admin/fx/rates', data),

  deactivateRate: (id: string) =>
    apiClient.delete(`/admin/fx/rates/${id}`),

  // Markup rules
  getMarkupRules: (providerId?: string) =>
    apiClient.get<FxMarkupRule[]>('/admin/fx/markup-rules', { params: { providerId } }),

  createMarkupRule: (data: {
    providerId: string;
    sourceCurrency?: string;
    targetCurrency?: string;
    markupType: string;
    markupValue: number;
    minAmount?: number;
    maxAmount?: number;
    effectiveFrom: string;
    effectiveTo?: string;
  }) => apiClient.post<FxMarkupRule>('/admin/fx/markup-rules', data),

  deactivateMarkupRule: (id: string) =>
    apiClient.delete(`/admin/fx/markup-rules/${id}`),
};
